type LegacyGuestSiteReloadInputs = Readonly<{
  /** `Event.featureFlags` as loaded by the guest site query. */
  featureFlags: ReadonlyArray<string | null> | null | undefined;
  /** `Event.useLegacyGuestSite` — the legacy field, distinct from the flag of the same name. */
  useLegacyGuestSite: boolean | null | undefined;
}>;

/**
 * Whether the mounted charm bundle should hard-reload so the server can hand the request to the
 * legacy (nginx) guest renderer. Mirrors the server's renderer decision
 * (`classifyGuestSiteRenderer` in src/server/utils/guestSiteRouting.ts) — the two must agree, or
 * the page reloads forever:
 *
 * - `useWebsiteV2` wins outright: the server always serves a V2 event through charm (carbon proxy
 *   or charm's own unpublished gate), so a stale legacy marker on the event must NOT trigger a
 *   reload — the server would serve the same charm page again, on every mount.
 * - otherwise `useCharm` pins charm; the legacy flag or field means legacy.
 *
 * Extracted so the flag interactions are unit-testable without mounting the guest site.
 */
export const shouldReloadForLegacyGuestSite = ({ featureFlags, useLegacyGuestSite }: LegacyGuestSiteReloadInputs): boolean => {
  const flags: ReadonlyArray<string | null> = featureFlags ?? [];
  if (flags.includes('useWebsiteV2')) {
    return false;
  }
  if (flags.includes('useCharm')) {
    return false;
  }
  return flags.includes('useLegacyGuestSite') || Boolean(useLegacyGuestSite);
};
